import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { get, set } from 'idb-keyval';
import { supabase } from '../supabase';

const GenerationContext = createContext(null);

export const GenerationProvider = ({ children }) => {
    // Dictionary of tasks: { [taskId]: { id, title, status: 'generating' | 'success' | 'error', result, error } }
    const [tasks, setTasks] = useState({});
    const [isLoaded, setIsLoaded] = useState(false);
    const cancelledTasksRef = useRef(new Set());
    const wakeLockRef = useRef(null);

    // Load persisted tasks from IDB on mount
    useEffect(() => {
        const loadTasks = async () => {
            try {
                const savedTasks = await get('generation_tasks');
                if (savedTasks) {
                    setTasks(savedTasks);
                }
            } catch (e) {
                console.error('Failed to load tasks from IDB:', e);
            } finally {
                setIsLoaded(true);
            }
        };
        loadTasks();
    }, []);

    // Persist tasks to IDB whenever they change
    useEffect(() => {
        if (isLoaded) {
            set('generation_tasks', tasks).catch(e => console.error('Failed to save tasks to IDB:', e));
        }
    }, [tasks, isLoaded]);

    const addManualTask = useCallback((taskId, title) => {
        setTasks(prev => {
            if (prev[taskId]?.status === 'generating') {
                return prev;
            }
            return {
                ...prev,
                [taskId]: { id: taskId, title, status: 'generating', result: null, error: null }
            };
        });
    }, []);

    const updateManualTask = useCallback((taskId, status, result = null, error = null) => {
        setTasks(prev => {
            if (!prev[taskId]) return prev;

            const title = prev[taskId].title;
            if (status === 'success') {
                toast.success(`${title} completed!`);
            } else if (status === 'error') {
                toast.error(`${title} failed: ${error || 'Unknown error'}`);
            }

            return {
                ...prev,
                [taskId]: { ...prev[taskId], status, result, error }
            };
        });
    }, []);

    const startGeneration = useCallback(async (taskId, title, asyncFn) => {
        // Prevent starting if already generating
        if (tasks[taskId]?.status === 'generating') {
            return;
        }

        setTasks(prev => ({
            ...prev,
            [taskId]: { id: taskId, title, status: 'generating', result: null, error: null }
        }));

        try {
            // Remove from cancelled set if we are restarting a task with same ID
            cancelledTasksRef.current.delete(taskId);

            // Pass a checking function so the callback can voluntarily abort if needed
            const result = await asyncFn(() => {
                 return cancelledTasksRef.current.has(taskId);
            });

            // Check if the task was soft-cancelled or cleared while asyncFn was running
            if (cancelledTasksRef.current.has(taskId)) {
                return; // Silently exit
            }

            setTasks(prev => {
                if (!prev[taskId]) return prev;
                return {
                    ...prev,
                    [taskId]: { ...prev[taskId], status: 'success', result }
                };
            });

            // Log feature usage
            supabase.auth.getUser().then(async ({ data }) => {
                if (data?.user) {
                    const { error } = await supabase.rpc('log_feature_usage', { user_id: data.user.id, feature_name: title });
                    if (error) console.error(error);
                }
            }).catch(console.error);

            toast.success(`${title} completed!`);
            return result;
        } catch (error) {
            if (cancelledTasksRef.current.has(taskId)) {
                return; // Silently exit if cancelled
            }

            setTasks(prev => {
                if (!prev[taskId]) return prev;
                return {
                    ...prev,
                    [taskId]: { ...prev[taskId], status: 'error', error: error.message || 'Generation failed' }
                };
            });

            toast.error(`${title} failed: ${error.message || 'Unknown error'}`);
            throw error;
        }
    }, []);

    const clearTask = useCallback((taskId) => {
        cancelledTasksRef.current.add(taskId); // Treat clear as cancel to prevent ghost toasts
        setTasks(prev => {
            const newTasks = { ...prev };
            delete newTasks[taskId];
            return newTasks;
        });
    }, []);

    const cancelTask = useCallback((taskId) => {
        cancelledTasksRef.current.add(taskId);
        setTasks(prev => {
            const newTasks = { ...prev };
            delete newTasks[taskId];
            return newTasks;
        });
        toast("Generation cancelled.", { icon: '🛑' });
    }, []);

    const activeTasks = Object.values(tasks).filter(t => t.status === 'generating');
    const completedTasks = Object.values(tasks).filter(t => t.status === 'success' || t.status === 'error');

    useEffect(() => {
        const requestWakeLock = async () => {
            if ('wakeLock' in navigator) {
                try {
                    wakeLockRef.current = await navigator.wakeLock.request('screen');
                    wakeLockRef.current.addEventListener('release', () => {
                        console.log('Screen Wake Lock released');
                    });
                    console.log('Screen Wake Lock acquired');
                } catch (err) {
                    console.error(`Wake Lock error: ${err.name}, ${err.message}`);
                }
            }
        };

        const releaseWakeLock = async () => {
            if (wakeLockRef.current !== null) {
                await wakeLockRef.current.release();
                wakeLockRef.current = null;
            }
        };

        if (activeTasks.length > 0 && !wakeLockRef.current) {
            requestWakeLock();
        } else if (activeTasks.length === 0 && wakeLockRef.current) {
            releaseWakeLock();
        }

        const handleVisibilityChange = async () => {
            if (wakeLockRef.current !== null && document.visibilityState === 'visible' && activeTasks.length > 0) {
                requestWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [activeTasks.length]);

    return (
        <GenerationContext.Provider value={{ tasks, activeTasks, completedTasks, startGeneration, clearTask, cancelTask, addManualTask, updateManualTask }}>
            {children}
        </GenerationContext.Provider>
    );
};

export const useGeneration = () => {
    const context = useContext(GenerationContext);
    if (!context) {
        throw new Error('useGeneration must be used within a GenerationProvider');
    }
    return context;
};
